import type {
  DismissalReason,
  GuidanceDto,
  GuidanceHistoryAction,
  PlatformCause,
  RecommendationState,
} from '@ederest/contracts';

export type Tone = 'brand' | 'positive' | 'neutral' | 'warning' | 'negative' | 'teal';

/** Product labels of the guidance states (functional §8.4, product decision). */
export const STATE_LABEL: Record<GuidanceDto['state'], { text: string; tone: Tone }> = {
  proposed: { text: 'Proposée', tone: 'brand' },
  approved: { text: 'Validée', tone: 'warning' },
  published: { text: 'Publiée', tone: 'positive' },
  disabled: { text: 'Désactivée', tone: 'neutral' },
  dismissed: { text: 'Écartée', tone: 'neutral' },
  deleted: { text: 'Supprimée', tone: 'neutral' },
};

export const RECOMMENDATION_STATE_LABEL: Record<RecommendationState, { text: string; tone: Tone }> = {
  pending: { text: 'En attente', tone: 'brand' },
  accepted: { text: 'Acceptée', tone: 'positive' },
  adjusted: { text: 'Ajustée', tone: 'positive' },
  dismissed: { text: 'Écartée', tone: 'neutral' },
};

export const COMPONENT_LABEL: Record<GuidanceDto['component'], string> = {
  help_bubble: "Bulle d'aide",
  guided_tour: 'Parcours guidé',
  checklist: 'Liste de contrôle',
  assisted_field: 'Champ assisté',
  progress_indicator: 'Indicateur de progression',
  warning: 'Avertissement',
};

/** The five reasons of functional §5.6, in the product's wording. */
export const DISMISSAL_REASON_LABEL: Record<DismissalReason, string> = {
  not_relevant: 'Non pertinente',
  already_handled: 'Déjà traitée autrement',
  out_of_guidance_scope: 'Hors périmètre du guidage',
  revisit_later: 'À revoir plus tard',
  other: 'Autre',
};

export const PLATFORM_CAUSE_LABEL: Record<PlatformCause, string> = {
  missing_field_validation: 'Validation de champ absente',
  unclear_label: 'Libellé peu clair',
  missing_document_list: 'Liste des pièces absente',
  technical_dead_end: 'Impasse technique',
};

export const CONDITION_LABEL: Record<GuidanceDto['trigger']['conditions'][number]['kind'], string> = {
  on_step_enter: "À l'entrée dans l'étape",
  on_field_focus: 'À la prise de focus',
  on_dwell_time: 'Au temps passé',
  on_friction_detected: 'Sur friction détectée',
  on_significant_interaction: 'Après une interaction',
  on_user_request: 'À la demande',
};

export const SIGNAL_LABEL: Record<string, string> = {
  hesitation: 'hésitation',
  repeated_click: 'clics répétés',
  dead_click: 'clic sans effet',
  error_state: 'erreur de saisie',
};

export const HISTORY_ACTION_LABEL: Record<GuidanceHistoryAction, string> = {
  'guidance.proposed': 'Proposée par le moteur',
  'guidance.updated': 'Ajustée',
  'guidance.approved': 'Validée',
  'guidance.reverted': 'Renvoyée en proposition',
  'guidance.dismissed': 'Écartée',
  'guidance.published': 'Publiée',
  'guidance.disabled': 'Désactivée',
  'guidance.deleted': 'Supprimée',
};

export const LANGUAGE_LABEL: Record<string, string> = { fr: 'Français', ar: 'Arabe', zgh: 'Amazighe' };

export function isRtl(language: string): boolean {
  return ['ar', 'he', 'fa', 'ur'].includes(language);
}

/** Read-only wording of a composed rule (technical §7.4). */
export function describeTrigger(trigger: GuidanceDto['trigger']): string {
  return trigger.conditions
    .map((condition) => {
      let label = CONDITION_LABEL[condition.kind].toLowerCase();
      if (condition.frictionSignal)
        label += ` (${SIGNAL_LABEL[condition.frictionSignal] ?? condition.frictionSignal})`;
      if (condition.dwellTimeMs !== null) label += ` ${Math.round(condition.dwellTimeMs / 1000)} s`;
      if (condition.targetSelector) label += ` sur ${condition.targetSelector}`;
      return label;
    })
    .join(' ET ');
}

/** Languages whose written body is missing: what blocks publication (functional §13.1 rule 5). */
export function missingLanguages(guidance: GuidanceDto, languages: readonly string[]): string[] {
  return languages.filter((code) => !guidance.contents[code]?.body?.trim());
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}
